import { HTMLProps, useMemo } from "react";
import { SchedulerLesson } from "../../models/schedule/lesson";
import { ISchedulerViewMode } from "../ui-lib/scheduler/ISchedulerViewMode";
import classNames from "classnames";

type LessonViewerInputProps = {
  lessons: SchedulerLesson[][];
  startDate: Date;
  viewMode: ISchedulerViewMode<SchedulerLesson>;
  neutralColor?: string;
  teacherId?: string;
} & HTMLProps<HTMLDivElement>;

function LessonViewer({
  viewMode,
  lessons,
  startDate,
  className,
  neutralColor = "bg-nid_grey",
  teacherId,
  ...props
}: LessonViewerInputProps) {
  const displayLessons = useMemo(
    () =>
      viewMode.displayLessons(startDate, lessons).map((lessons) =>
        lessons.map((lesson) => ({
          ...lesson,
          isApplied: teacherId
            ? lesson.teacherApplications
                ?.map((teacher) => teacher.id)
                .includes(teacherId) ?? false
            : !!lesson.teacherApplications?.length,
          isAccepted: teacherId
            ? lesson.teacherAccepted?.id === teacherId
            : !!lesson.teacherAccepted,
          isDeclined:
            !!teacherId &&
            (lesson.teacherDeclined
              ?.map((teacher) => teacher.id)
              .includes(teacherId) ??
              false),
        })),
      ),
    [lessons, startDate, teacherId, viewMode],
  );

  const evaluateLessonColor = (
    lessons: {
      isActive: boolean;
      isApplied: boolean;
      isAccepted: boolean;
      isDeclined: boolean;
    }[],
  ) => {
    if (lessons.some((l) => l.isApplied)) {
      return "bg-th-accent-900";
    } else if (lessons.some((l) => l.isAccepted)) {
      return "bg-th-secondary-900";
    } else if (lessons.some((l) => l.isDeclined)) {
      return "bg-red-600";
    } else if (lessons.some((l) => l.isActive)) {
      return "bg-th-primary-900";
    } else {
      return neutralColor;
    }
  };

  return (
    <div className={`${className} flex`} {...props}>
      {displayLessons.map((dl, i) => (
        <div className="mr-1 space-y-1" key={`${i}_lesson_viewer_lessons`}>
          <div
            className={classNames(
              "h-5 w-5 rounded-full",
              evaluateLessonColor(dl.slice(0, dl.length / 2)),
            )}
          ></div>
          <div
            className={classNames(
              "h-5 w-5 rounded-full",
              evaluateLessonColor(dl.slice(dl.length / 2 + 1, dl.length)),
            )}
          ></div>
        </div>
      ))}
    </div>
  );
}

export default LessonViewer;
